import React, { useEffect, useMemo, useState } from "react";

import { InlineSpinner } from "./loading";
import { SkeletonRows } from "./skeleton";

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type MemoryDocument = {
  id: string;
  custom_id?: string | null;
  container_tags: string[];
  scope: string;
  kind?: string | null;
  repository?: string | null;
  source?: string | null;
  fingerprint?: string | null;
  title?: string | null;
  summary: string;
  content?: string | null;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type MemorySnapshot = {
  enabled: boolean;
  handoff: boolean;
  saved_enabled?: boolean;
  saved_handoff?: boolean;
  documents: MemoryDocument[];
  page: number;
  limit: number;
  total: number;
  error?: string | null;
};

const SCOPES = [
  { value: "all", label: "All containers" },
  { value: "netra:org", label: "Organisation" },
];

export function formatWhen(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().replace("T", " ").slice(0, 16);
}

export function previewText(document: MemoryDocument, max = 180) {
  const text = (document.summary || document.content || "").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text || "No summary";
  return `${text.slice(0, max - 1)}…`;
}

export function memoryListUrl(page: number, container: string) {
  const params = new URLSearchParams({ page: String(page), limit: "50" });
  if (container !== "all") params.set("container", container);
  return `/api/v1/memories?${params.toString()}`;
}

export function emptyMemorySnapshot(page: number, error?: string): MemorySnapshot {
  return {
    enabled: false,
    handoff: false,
    documents: [],
    page,
    limit: 50,
    total: 0,
    error,
  };
}

export function filterMemoryDocuments(documents: MemoryDocument[], query: string) {
  const needle = query.trim().toLowerCase();
  if (!needle) return documents;
  return documents.filter((row) =>
    [row.summary, row.content, row.title, row.kind, row.repository, row.scope, row.source, row.custom_id]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(needle),
  );
}

export async function fetchMemorySnapshot(
  request: FetchLike,
  page: number,
  container: string,
): Promise<MemorySnapshot> {
  const response = await request(memoryListUrl(page, container), { cache: "no-store" });
  const body = (await response.json().catch(() => ({}))) as MemorySnapshot & { error?: string };
  if (!response.ok) {
    return emptyMemorySnapshot(page, body.error || `API ${response.status}`);
  }
  return body;
}

export async function fetchMemoryDocument(request: FetchLike, id: string) {
  const response = await request(`/api/v1/memories/${encodeURIComponent(id)}`, { cache: "no-store" });
  if (!response.ok) return null;
  const body = (await response.json().catch(() => null)) as MemoryDocument | null;
  return body?.id ? body : null;
}

export async function forgetMemoryDocument(request: FetchLike, id: string) {
  const response = await request(`/api/v1/memories/${encodeURIComponent(id)}`, { method: "DELETE" });
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    return { ok: false as const, error: body.error || `Forget failed (${response.status})` };
  }
  return { ok: true as const };
}

export async function saveMemoryPolicy(
  request: FetchLike,
  key: "SUPERMEMORY_ENABLED" | "SUPERMEMORY_HANDOFF",
  value: boolean,
) {
  const response = await request("/api/v1/settings", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ values: { [key]: value } }),
  });
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    return { ok: false as const, error: body.error || `Could not save ${key} (${response.status})` };
  }
  const label = key === "SUPERMEMORY_ENABLED" ? "SuperMemory recall" : "Failover handoff";
  return { ok: true as const, message: `${label} saved. Restart Netra to apply it.` };
}

export function MemorySettings({
  fetchImpl,
  initialSnapshot,
  initialSelectedId,
  onPolicyChanged,
}: {
  fetchImpl?: FetchLike;
  initialSnapshot?: MemorySnapshot | null;
  initialSelectedId?: string;
  onPolicyChanged?: (restartRequired: boolean, notice: string) => void;
} = {}) {
  const request = fetchImpl ?? fetch;
  const [snapshot, setSnapshot] = useState<MemorySnapshot | null>(initialSnapshot ?? null);
  const [container, setContainer] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [policyBusy, setPolicyBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
  const [detail, setDetail] = useState<MemoryDocument | null>(
    initialSnapshot?.documents.find((document) => document.id === initialSelectedId) ?? null,
  );

  const load = async (nextPage = page, nextContainer = container) => {
    setNotice("");
    setSnapshot(await fetchMemorySnapshot(request, nextPage, nextContainer));
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const body = await fetchMemorySnapshot(request, 1, container);
      if (cancelled) return;
      setSnapshot(body);
      setPage(1);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [container, request]);

  const documents = useMemo(
    () => filterMemoryDocuments(snapshot?.documents ?? [], query),
    [snapshot, query],
  );

  const inspect = async (document: MemoryDocument) => {
    setSelectedId(document.id);
    setDetail(document);
    setNotice("");
    const body = await fetchMemoryDocument(request, document.id);
    if (body) setDetail(body);
  };

  const forget = async (document: MemoryDocument) => {
    if (!window.confirm(`Forget this memory?\n\n${previewText(document, 240)}\n\nIt will no longer be injected into later review prompts.`)) {
      return;
    }
    setBusyId(document.id);
    setNotice("");
    try {
      const result = await forgetMemoryDocument(request, document.id);
      if (!result.ok) {
        setNotice(result.error);
        return;
      }
      if (selectedId === document.id) {
        setSelectedId(null);
        setDetail(null);
      }
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const setPolicy = async (key: "SUPERMEMORY_ENABLED" | "SUPERMEMORY_HANDOFF", value: boolean) => {
    setPolicyBusy(key);
    setNotice("");
    try {
      const result = await saveMemoryPolicy(request, key, value);
      if (!result.ok) {
        setNotice(result.error);
        return;
      }
      setSnapshot((current) => current ? {
        ...current,
        saved_enabled: key === "SUPERMEMORY_ENABLED" ? value : current.saved_enabled,
        saved_handoff: key === "SUPERMEMORY_HANDOFF" ? value : current.saved_handoff,
      } : current);
      setNotice(result.message);
      onPolicyChanged?.(true, result.message);
    } finally {
      setPolicyBusy(null);
    }
  };

  const liveEnabled = snapshot?.enabled ?? false;
  const savedEnabled = snapshot?.saved_enabled ?? liveEnabled;
  const savedHandoff = snapshot?.saved_handoff ?? snapshot?.handoff ?? false;
  const totalPages = Math.max(1, Math.ceil((snapshot?.total ?? 0) / Math.max(snapshot?.limit ?? 50, 1)));
  const selected = detail ?? documents.find((document) => document.id === selectedId) ?? null;
  const pendingRestart = (snapshot != null)
    && (savedEnabled !== liveEnabled || savedHandoff !== (snapshot.handoff ?? false));

  return (
    <section className="settings-grid memories-grid">
      <div className="activity-card detail-section">
        <p className="eyebrow">SHARED MEMORY</p>
        <div className="integration-title">
          <div>
            <h2>Organisational recall</h2>
            <p>Curated learnings SuperMemory injects into every provider prompt.</p>
          </div>
          <span
            className={`connection-state ${liveEnabled ? "connected" : ""}`}
            aria-busy={snapshot == null ? "true" : undefined}
          >
            {/* "injecting" and "not injecting" are answers; the third state is
                not knowing yet, and it should not look like one of them. */}
            {snapshot == null
              ? <><InlineSpinner label="Reading memory policy" labelled={false} /> reading</>
              : liveEnabled ? "injecting" : "not injecting"}
          </span>
        </div>
        <div className="typed-settings memory-policy">
          <label className="toggle-control inversion-toggle">
            <span>Inject memories into review prompts</span>
            <input
              type="checkbox"
              checked={savedEnabled}
              disabled={policyBusy === "SUPERMEMORY_ENABLED" || snapshot == null}
              onChange={(event) => void setPolicy("SUPERMEMORY_ENABLED", event.target.checked)}
            />
            <i />
          </label>
          <label className="toggle-control inversion-toggle">
            <span>Write failover handoff notes</span>
            <input
              type="checkbox"
              checked={savedHandoff}
              disabled={policyBusy === "SUPERMEMORY_HANDOFF" || snapshot == null}
              onChange={(event) => void setPolicy("SUPERMEMORY_HANDOFF", event.target.checked)}
            />
            <i />
          </label>
        </div>
        <dl className="settings-facts">
          <dt>Live recall</dt>
          <dd>{liveEnabled ? "on" : "off"}</dd>
          <dt>Live handoff</dt>
          <dd>{snapshot?.handoff ? "on" : "off"}</dd>
          <dt>Stored documents</dt>
          <dd>{snapshot?.total ?? "—"}</dd>
        </dl>
        {pendingRestart && (
          <p className="settings-note">Saved policy differs from the running process. Restart Netra to apply the toggle.</p>
        )}
        {!liveEnabled && snapshot && (
          <p className="settings-note">
            SuperMemory is not injecting right now. Enable it here, keep <code>supermemory-server</code> running,
            and restart Netra. Review jobs fail open if the store is down.
          </p>
        )}
        {snapshot?.error && <pre className="failure">{snapshot.error}</pre>}
        {notice && <pre className="failure">{notice}</pre>}
      </div>
      <div className="activity-card detail-section memories-table-card">
        <div className="section-heading">
          <div>
            <p className="eyebrow">CORPUS</p>
            <h2>Memories</h2>
          </div>
          <button className="secondary-action" type="button" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        <div className="memories-toolbar">
          <label>
            <span>Container</span>
            <select
              value={container}
              onChange={(event) => {
                setPage(1);
                setContainer(event.target.value);
              }}
            >
              {SCOPES.map((scope) => (
                <option key={scope.value} value={scope.value}>
                  {scope.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Filter this page</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="repo, class, or summary"
            />
          </label>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Scope</th>
                <th>Kind</th>
                <th>Memory</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {/* Distinguished on purpose. "No memories match this filter" is a
                  result; before the snapshot arrives there is no result yet,
                  and a table that says the filter matched nothing while it is
                  still being read sends the reader off to fix a filter that
                  was never the problem. */}
              {snapshot == null ? (
                <SkeletonRows label="Loading memories" columns={5} />
              ) : documents.length === 0 ? (
                <tr>
                  <td className="empty" colSpan={5}>
                    {liveEnabled
                      ? "No memories match this filter."
                      : "SuperMemory is not injecting memories."}
                  </td>
                </tr>
              ) : (
                documents.map((document) => (
                  <tr
                    key={document.id}
                    className={selectedId === document.id ? "memory-row selected" : "memory-row"}
                    onClick={() => void inspect(document)}
                  >
                    <td>{formatWhen(document.created_at)}</td>
                    <td>
                      <strong>{document.scope}</strong>
                      {document.repository && document.scope !== document.repository && (
                        <span>{document.repository}</span>
                      )}
                    </td>
                    <td className="memory-kind">{document.kind || document.source || "—"}</td>
                    <td>
                      <strong>{document.title || document.kind || document.id}</strong>
                      <span>{previewText(document)}</span>
                    </td>
                    <td className="preview-resource-actions">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void inspect(document);
                        }}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        disabled={!liveEnabled || busyId === document.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          void forget(document);
                        }}
                      >
                        {busyId === document.id ? "Forgetting…" : "Forget"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {snapshot && snapshot.total > snapshot.limit && (
          <div className="memories-pager">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => {
                const next = page - 1;
                setPage(next);
                void load(next);
              }}
            >
              Previous
            </button>
            <span>
              Page {snapshot.page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => {
                const next = page + 1;
                setPage(next);
                void load(next);
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
      {selected && (
        <div className="drawer-backdrop" onClick={() => { setSelectedId(null); setDetail(null); }}>
          <aside className="drawer memory-drawer" onClick={(event) => event.stopPropagation()}>
            <button className="drawer-close" type="button" onClick={() => { setSelectedId(null); setDetail(null); }}>×</button>
            <p className="eyebrow">SELECTED MEMORY</p>
            <h2>{selected.title || selected.kind || selected.id}</h2>
            <dl>
              <dt>Scope</dt><dd>{selected.scope}</dd>
              <dt>Kind</dt><dd>{selected.kind || "—"}</dd>
              <dt>Source</dt><dd>{selected.source || "—"}</dd>
              <dt>Repository</dt><dd>{selected.repository || "—"}</dd>
              <dt>Status</dt><dd>{selected.status || "—"}</dd>
              <dt>Created</dt><dd>{formatWhen(selected.created_at)}</dd>
              <dt>Updated</dt><dd>{formatWhen(selected.updated_at)}</dd>
              <dt>Custom id</dt><dd><code>{selected.custom_id || "—"}</code></dd>
              <dt>Fingerprint</dt><dd><code>{selected.fingerprint || "—"}</code></dd>
              <dt>Document id</dt><dd><code>{selected.id}</code></dd>
              <dt>Containers</dt><dd>{selected.container_tags.join(", ") || "—"}</dd>
            </dl>
            {selected.summary ? (
              <>
                <p className="eyebrow">SUMMARY</p>
                <pre className="memory-body">{selected.summary}</pre>
              </>
            ) : null}
            <p className="eyebrow">FULL CONTENT</p>
            <pre className="memory-body">{selected.content || selected.summary || "No content was stored for this document."}</pre>
            <div className="drawer-actions">
              <button
                className="danger-button"
                type="button"
                disabled={!liveEnabled || busyId === selected.id}
                onClick={() => void forget(selected)}
              >
                {busyId === selected.id ? "Forgetting…" : "Forget this memory"}
              </button>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
